import { ReactNode } from 'react'
import cx from 'classnames'

import Styles from './Checkbox.module.scss'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useFocusRing } from '@react-aria/focus'
import { useToggleState } from '@react-stately/toggle'
import { useCheckbox } from '@react-aria/checkbox'
import { AriaCheckboxProps, ToggleProps } from '@react-types/checkbox'
import { useObjectRef } from 'utils/useObjectRef'
import { CheckboxProps } from './Types'
import { useField } from 'formik'
import { Check } from 'blaze-ui/icons/16'
import { AriaLabelingProps } from '@react-types/shared'

export interface Props extends CheckboxProps, ToggleProps, AriaCheckboxProps, Pick<AriaLabelingProps, 'aria-label'> {
  checkIcon?: ReactNode
  extraHitArea?: boolean
  disableFocus?: boolean
}

interface FormikCheckboxProps extends Props {}

export const Checkbox = (props: Props) => {
  let state = useToggleState(props)
  let ref = useObjectRef<HTMLInputElement>()
  let { inputProps } = useCheckbox(props, state, ref)
  let { isFocusVisible, focusProps } = useFocusRing()

  const { className, size = 'sm', variant = 'primary', checkIcon = <Check /> } = props

  return (
    <label
      className={cx(Styles.label, className, {
        [Styles.extraHitArea]: !!props.extraHitArea,
        [Styles.disabled]: props.isDisabled,
      })}
    >
      <VisuallyHidden>
        <input {...inputProps} {...focusProps} ref={ref} />
      </VisuallyHidden>
      <div
        className={cx(Styles.checkbox, Styles[size], Styles[variant], {
          [Styles.selected]: state.isSelected,
          [Styles.focused]: !props.disableFocus && isFocusVisible,
        })}
      >
        {state.isSelected && checkIcon}
        {size !== 'sm' && !state.isSelected && <div className={Styles.hoveredCheckMark}>{checkIcon}</div>}
      </div>
      {props.children}
    </label>
  )
}

export const FormikCheckbox = (props: FormikCheckboxProps) => {
  const [field, meta] = useField(props.name as string)
  const displayError = meta.touched && meta.error
  const enhancedProps = {
    ...props,
    ...field,
    ...(displayError && { errorMessage: meta.error }),
    isSelected: field.value,
    onChange: props.onChange,
  }
  return <Checkbox {...enhancedProps} />
}
